import type { ReactNode, Ref, SVGProps } from 'react'

import { type Glyph, ICON_GRID, iconPixels, type IconSize, iconStrokeWidth } from '@gto/icons'

export type IconProps = {
  glyph: Glyph
  /**
   * A size token, or a number of pixels. Without it the icon has no `width` and no `height`:
   * a slot, a size class or the wrapper gives the size.
   */
  size?: IconSize | number
  /** Gives the icon a name for assistive technology. Without it the icon is decorative. */
  title?: string
  ref?: Ref<SVGSVGElement>
} & Omit<SVGProps<SVGSVGElement>, 'children' | 'ref'>

type IconRootProps = IconProps & {
  children: ReactNode
}

/**
 * The `<svg>` of an icon, without its paths. `Icon` and `AnimatedIcon` draw the paths.
 *
 * The root has no class of its own, and `size` sets the `width` and `height` attributes.
 * A design-system slot sizes a child `svg` with a class such as `[&>svg]:size-5`, and a
 * class is stronger than an attribute.
 *
 * With no `size` the root has no `width` and no `height`, as the Heroicons components
 * that it replaces. The icon then fills its wrapper unless a slot or a class sizes it.
 */
export const IconRoot = ({ glyph, size, title, ref, children, ...props }: IconRootProps) => {
  const pixels = size === undefined ? undefined : iconPixels(size)
  const isOutline = glyph.version === 'outline'

  return (
    <svg
      ref={ref}
      aria-hidden={title === undefined ? true : undefined}
      data-slot="icon"
      fill={isOutline ? 'none' : 'currentColor'}
      height={pixels}
      role={title === undefined ? undefined : 'img'}
      stroke={isOutline ? 'currentColor' : undefined}
      strokeLinecap={isOutline ? 'round' : undefined}
      strokeLinejoin={isOutline ? 'round' : undefined}
      strokeWidth={isOutline ? iconStrokeWidth(size) : undefined}
      viewBox={`0 0 ${ICON_GRID} ${ICON_GRID}`}
      width={pixels}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {title !== undefined && <title>{title}</title>}
      {children}
    </svg>
  )
}
